import { Routes, Route } from 'react-router-dom';
import { Typography, Box, Card, CardContent } from '@mui/material';
import { MainLayout } from './components/layout/MainLayout';
import { Settings } from './pages/Settings';
import { WorkspaceManager } from './components/WorkspaceManager';
import { AuthProvider } from './contexts/AuthContext';
import { ProtectedRoute } from './components/auth/ProtectedRoute';
import { ENVIRONMENT_CONFIG, envLog } from './config/environment';

// Debug environment variables on app startup
console.log('=== DEBUG: Environment variables ===', {
  VITE_API_ENDPOINT: import.meta.env.VITE_API_ENDPOINT,
  VITE_USER_POOL_ID: import.meta.env.VITE_USER_POOL_ID,
  VITE_USER_POOL_CLIENT_ID: import.meta.env.VITE_USER_POOL_CLIENT_ID,
  VITE_AWS_REGION: import.meta.env.VITE_AWS_REGION,
  MODE: import.meta.env.MODE,
  DEV: import.meta.env.DEV,
  PROD: import.meta.env.PROD,
});

// Log environment info on app startup
envLog.info('TimeLynxx Admin Portal starting up', {
  environment: ENVIRONMENT_CONFIG.environment,
  subdomain: ENVIRONMENT_CONFIG.subdomain,
  features: ENVIRONMENT_CONFIG.features,
});

/**
 * TimeLynxx Admin Portal
 * Multi-tenant time-tracking administration with authentication
 */
function App() {
  return (
    <AuthProvider>
      <ProtectedRoute>
        <MainLayout>
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/workspaces" element={<Workspaces />} />
            <Route path="/users" element={<Users />} />
            <Route path="/projects" element={<Projects />} />
            <Route path="/time-entries" element={<TimeEntries />} />
            <Route path="/analytics" element={<Analytics />} />
            <Route path="/settings" element={<Settings />} />
          </Routes>
        </MainLayout>
      </ProtectedRoute>
    </AuthProvider>
  );
}

// Placeholder components
function Dashboard() {
  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Dashboard
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Welcome to TimeLynxx Admin Portal - Track Your Time. Build Your Future.
      </Typography>
      {ENVIRONMENT_CONFIG.enableDebugMode && (
        <Card sx={{ mb: 2, bgcolor: 'warning.light', color: 'warning.contrastText' }}>
          <CardContent>
            <Typography variant="body2">
              🔧 Running in {ENVIRONMENT_CONFIG.environment} mode ({ENVIRONMENT_CONFIG.subdomain})
            </Typography>
          </CardContent>
        </Card>
      )}
      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Quick Stats
          </Typography>
          <Typography variant="body2">
            Dashboard content will be implemented with real data connectivity.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}

function Workspaces() {
  return <WorkspaceManager />;
}

function Users() {
  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Users
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Manage workspace users, roles, and permissions
      </Typography>
      <Card>
        <CardContent>
          <Typography variant="body2">
            User management interface will be implemented here.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}

function Projects() {
  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Projects
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Manage time tracking projects and categories
      </Typography>
      <Card>
        <CardContent>
          <Typography variant="body2">
            Project management interface will be implemented here.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}

function TimeEntries() {
  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Time Entries
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Review and manage time tracking entries
      </Typography>
      <Card>
        <CardContent>
          <Typography variant="body2">
            Time entry management interface will be implemented here.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}

function Analytics() {
  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Analytics
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        View detailed time tracking reports and analytics
      </Typography>
      <Card>
        <CardContent>
          <Typography variant="body2">
            Analytics and reporting interface will be implemented here.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
}

export default App;